/**
 * The rewrite's status summary (DESIGN UD-20, adapted to bench v3 and ED-86): opened by a click anywhere on the status bar's
 * rewrite line while a rewrite runs, and from the same place after it ends. It shows what happens behind the scenes, live,
 * refreshed at least once a second:
 *
 * - the job (document, start, scope, model, effort, route, requests at once, versions, keep standard, brief, risk sheet);
 * - the progress (the round of at most four, the answers in, stretches finished, elapsed, time left);
 * - the requests (in flight, queued, the limit in force now and that it adapts, requests not applied, slowdowns);
 * - the stretches, one row each (waiting, request in flight, answer applied, finished), capped at {@link MAX_TABLE_ROWS} rows so
 *   700 stretches cost no long task (TEST-PLAN §9);
 * - the last events, newest first (answers applied, rounds started, slowdowns, the brief), the cost and what happens next.
 *
 * Everything shown is already on the main thread: the job record, the worker's progress messages (the round, the answers in, the
 * stretch list once a second, what the last applied answer did), the limiter's state and the budget guard. Text nodes only.
 */
import type { ComponentChildren } from 'preact';
import { useEffect, useState } from 'preact/hooks';
import type { ViewModel } from '../../shared/viewmodel.ts';
import { useEstimate } from '../components/Working.tsx';
import { fmt2, fmtElapsed, fmtInt } from '../format.ts';
import { stretchLabel } from '../model.ts';
import { SUMMARY } from '../strings-bench.ts';
import { Dialog } from '../ui.tsx';
import { type WorkItem, work } from '../work.ts';
import { ChannelIdsIn } from './TermIn.tsx';
import { cancelRun, summaryJob } from './controller.ts';
import { fmtJobTime, jobModelEffort } from './jobs-format.ts';
import { MAX_ROUNDS, type RunEvent, type RunLive, tableRows } from './run-live.ts';
import {
	budgetState,
	economyRound,
	parallelState,
	runLive,
	runProgress,
	runSummary,
	runSummaryOpen,
	stage,
} from './state.ts';

function useClock(on: boolean): number {
	const [now, setNow] = useState(performance.now());
	useEffect(() => {
		if (!on) return;
		const t = window.setInterval(() => setNow(performance.now()), 1000);
		return () => window.clearInterval(t);
	}, [on]);
	return now;
}

function scopeText(job: ReturnType<typeof summaryJob>): string {
	if (!job) return '';
	const s = job.scope;
	switch (s.kind) {
		case 'channel':
			return SUMMARY.scopes.channel(s.id);
		case 'hotspot':
			return SUMMARY.scopes.hotspot;
		case 'selection':
			return SUMMARY.scopes.selection;
		case 'translation':
			return SUMMARY.scopes.translation;
		case 'document':
			return SUMMARY.scopes.document;
		default:
			return SUMMARY.scopes.flagged;
	}
}

const clock = (at: number): string =>
	new Date(at).toLocaleTimeString('en-US', {
		hour: 'numeric',
		minute: '2-digit',
		second: '2-digit',
	});

/** One event as a sentence; a stretch is named with the reader's own label. */
function eventText(vm: ViewModel, e: RunEvent): string {
	const E = SUMMARY.event;
	const d = e.data;
	switch (d.kind) {
		case 'started':
			return E.started(fmtInt(d.total));
		case 'resumed':
			return E.resumed(fmtInt(d.done), fmtInt(d.total));
		case 'wave':
			return E.wave(String(d.wave), fmtInt(d.requests));
		case 'answer': {
			const where = stretchLabel(vm, d.a, d.b);
			if (!d.took) return E.skipped(where);
			return E.answer(
				where,
				String(d.round),
				fmtInt(d.accepted),
				d.rejected > 0 ? E.notAccepted(fmtInt(d.rejected)) : '',
				d.reason ? E.reason(d.reason) : ''
			);
		}
		case 'finished':
			return E.finished(stretchLabel(vm, d.a, d.b), SUMMARY.status[d.status]);
		case 'slowdown':
			return E.slowdown(String(d.limit));
		case 'brief':
			return E.brief;
	}
}

function EstimateRow({ item }: { item: WorkItem }) {
	const { estimate } = useEstimate(item);
	return (
		<div>
			<dt>{SUMMARY.estimate}</dt>
			<dd data-testid="run-summary-estimate">{estimate?.text ?? SUMMARY.estimateNone}</dd>
		</div>
	);
}

function Section({ title, children }: { title: string; children: ComponentChildren }) {
	return (
		<section class="run-summary-section">
			<h3>{title}</h3>
			{children}
		</section>
	);
}

/** The rows of the stretches table: at most 50, those in flight and the latest changes first. */
function StretchTable({ vm, live }: { vm: ViewModel; live: RunLive }) {
	const t = SUMMARY.table;
	const { rows, total, flying, waiting, finished } = tableRows(live);
	if (total === 0) return <p class="dim">{t.none}</p>;
	return (
		<table class="run-summary-table" data-testid="run-summary-stretches">
			<caption>
				{t.caption(
					fmtInt(rows.length),
					fmtInt(total),
					fmtInt(flying),
					fmtInt(waiting),
					fmtInt(finished)
				)}
			</caption>
			<thead>
				<tr>
					<th scope="col">{t.stretch}</th>
					<th scope="col">{t.state}</th>
					<th scope="col">{t.rounds}</th>
					<th scope="col">{t.open}</th>
					<th scope="col">{t.addressed}</th>
					<th scope="col">{t.failed}</th>
				</tr>
			</thead>
			<tbody>
				{rows.map(({ view: v, state }) => (
					<tr key={v.key} data-state={state}>
						<th scope="row">{stretchLabel(vm, v.a, v.b)}</th>
						<td>
							{state === 'flying'
								? t.inFlight(String(v.rounds + 1))
								: state === 'applied'
									? t.applied(String(v.rounds))
									: state === 'done' && v.done
										? t.finished(SUMMARY.status[v.done])
										: t.waiting}
						</td>
						<td>{v.rounds}</td>
						<td>{v.open}</td>
						<td>{v.addressed}</td>
						<td>{v.failed}</td>
					</tr>
				))}
			</tbody>
		</table>
	);
}

export function RunSummaryDialog({ vm }: { vm: ViewModel }) {
	const open = runSummaryOpen.value;
	const running = stage.value === 'running';
	const now = useClock(open && running);
	if (!open) return null;
	const job = summaryJob();
	const sum = runSummary.value;
	const prog = runProgress.value;
	const budget = budgetState.value;
	const round = economyRound.value;
	const lim = parallelState.value;
	const item = work.value.find((w) => w.id === 'rewrite');
	const startedAt = item?.startedAt ?? sum?.startedAt ?? now;
	const found = runLive.value;
	// the live record of THIS job only (a finished run's record stays until the next run starts)
	const live = found && (!job || found.jobId === job.id) ? found : null;
	const parallel = !!job && job.concurrency !== undefined && !job.economy;
	const title = running
		? SUMMARY.title
		: stage.value === 'done'
			? SUMMARY.titleDone
			: SUMMARY.titleStopped;
	const close = () => {
		runSummaryOpen.value = false;
	};
	const where = (a: number, b: number): string => stretchLabel(vm, a, b);
	const done = prog?.done ?? job?.doneChunks ?? 0;
	const total = prog?.total ?? job?.totalChunks ?? 0;
	return (
		<Dialog
			title={title}
			open={open}
			onClose={close}
			actions={
				<>
					{running ? (
						<button
							type="button"
							class="btn btn-secondary"
							data-testid="run-summary-cancel"
							onClick={() => {
								cancelRun();
								close();
							}}
						>
							{SUMMARY.cancel}
						</button>
					) : null}
					<button type="button" class="btn btn-primary" onClick={close}>
						{SUMMARY.close}
					</button>
				</>
			}
		>
			{!job && !sum ? (
				<p class="dim">{SUMMARY.noRun}</p>
			) : (
				<div class="run-summary" data-testid="run-summary">
					{job ? (
						<Section title={SUMMARY.job}>
							<dl class="kv">
								{job.docName ? (
									<div>
										<dt>{SUMMARY.document}</dt>
										<dd data-testid="run-summary-document">{job.docName}</dd>
									</div>
								) : null}
								<div>
									<dt>{SUMMARY.startedAt}</dt>
									<dd>{fmtJobTime(job.createdAt)}</dd>
								</div>
								<div>
									<dt>{SUMMARY.scope}</dt>
									<dd>
										<ChannelIdsIn text={scopeText(job)} />
									</dd>
								</div>
								<div>
									<dt>{SUMMARY.setup}</dt>
									<dd>
										{SUMMARY.setupLine(
											jobModelEffort(job),
											job.economy ? SUMMARY.routeEconomy : SUMMARY.routeStandard
										)}
									</dd>
								</div>
								<div>
									<dt>{SUMMARY.atOnce}</dt>
									<dd>
										{parallel ? SUMMARY.atOnceLine(String(job.concurrency)) : SUMMARY.atOnceEconomy}
									</dd>
								</div>
								<div>
									<dt>{SUMMARY.versions}</dt>
									<dd>{SUMMARY.versionsLine(String(job.versions ?? 1))}</dd>
								</div>
								<div>
									<dt>{SUMMARY.keep}</dt>
									<dd>{job.keep === 'lenient' ? SUMMARY.keepLenient : SUMMARY.keepStrict}</dd>
								</div>
								{job.guardrails ? (
									<div data-testid="summary-guardrails">
										<dt>{SUMMARY.guardrails}</dt>
										<dd>{SUMMARY.guardrailsOn}</dd>
									</div>
								) : null}
								<div>
									<dt>{SUMMARY.brief}</dt>
									<dd>{job.brief ? SUMMARY.briefOn : SUMMARY.briefOff}</dd>
								</div>
								<div>
									<dt>{SUMMARY.riskSheet}</dt>
									<dd>{SUMMARY.riskOn}</dd>
								</div>
							</dl>
						</Section>
					) : null}
					<Section title={SUMMARY.progress}>
						<dl class="kv">
							{parallel && live && live.wave > 0 ? (
								<div>
									<dt>{SUMMARY.round}</dt>
									<dd data-testid="run-summary-round">
										{live.phase === 'final'
											? SUMMARY.roundFinal
											: live.phase === 'top-up'
												? SUMMARY.roundTopUp
												: SUMMARY.roundLine(
														String(live.wave),
														String(MAX_ROUNDS),
														fmtInt(live.waveDone),
														fmtInt(live.waveDone + live.wavePending),
														fmtInt(live.wavePending)
													)}
									</dd>
								</div>
							) : parallel && running ? (
								<div>
									<dt>{SUMMARY.round}</dt>
									<dd data-testid="run-summary-round">{SUMMARY.roundNone}</dd>
								</div>
							) : null}
							<div>
								<dt>{SUMMARY.stretches}</dt>
								<dd data-testid="run-summary-progress">
									{SUMMARY.progressLine(fmtInt(done), fmtInt(total))}
									{live && running
										? ` · ${SUMMARY.shareDone(`${Math.round(live.fraction * 100)}%`)}`
										: ''}
									{running ? ` · ${SUMMARY.elapsed(fmtElapsed(now - startedAt))}` : ''}
									{item?.detail && running && !parallel ? ` · ${item.detail}` : ''}
								</dd>
							</div>
							{running && item ? <EstimateRow item={item} /> : null}
							{round ? (
								<div>
									<dt>{SUMMARY.economy}</dt>
									<dd>{SUMMARY.economyLine(String(round.round), round.waiting)}</dd>
								</div>
							) : null}
							<div>
								<dt>{SUMMARY.outcomes}</dt>
								<dd data-testid="run-summary-outcomes">
									{sum && (prog?.done ?? 0) > 0
										? SUMMARY.outcomeLine(
												fmtInt(sum.tally.accepted),
												fmtInt(sum.tally.partial),
												fmtInt(sum.tally.failed),
												fmtInt(sum.tally.untouched)
											)
										: SUMMARY.outcomesNone}
								</dd>
							</div>
							{job?.guardrails && job.report ? (
								// the passages the editor left as they are (GR2): only for a finished run made with the guardrails
								<div data-testid="summary-declined">
									<dt>{SUMMARY.declined}</dt>
									<dd>{SUMMARY.declinedLine(fmtInt(job.report.declined?.length ?? 0))}</dd>
								</div>
							) : null}
							{sum?.last ? (
								<div>
									<dt>{SUMMARY.last}</dt>
									<dd>
										{SUMMARY.lastLine(
											where(sum.last.a, sum.last.b),
											SUMMARY.status[sum.last.status],
											fmtInt(sum.last.rounds)
										)}
										{sum.last.messages.map((m) => (
											<p key={m} class="dim">
												<ChannelIdsIn text={m} />
											</p>
										))}
									</dd>
								</div>
							) : null}
						</dl>
					</Section>
					{parallel && live ? (
						<Section title={SUMMARY.requests}>
							<dl class="kv" data-testid="run-summary-requests">
								<div>
									<dt>{SUMMARY.inFlight}</dt>
									<dd>
										{running
											? SUMMARY.inFlightLine(
													fmtInt(lim?.inFlight ?? live.inFlight),
													fmtInt(lim?.queued ?? 0)
												)
											: SUMMARY.inFlightLine('0', '0')}
									</dd>
								</div>
								{lim && running ? (
									<div>
										<dt>{SUMMARY.limit}</dt>
										<dd>
											{SUMMARY.limitLine(String(lim.limit), String(job?.concurrency ?? lim.limit))}
										</dd>
									</div>
								) : null}
								<div>
									<dt>{SUMMARY.wasted}</dt>
									<dd>{SUMMARY.wastedLine(fmtInt(live.superseded), fmtInt(live.skipped))}</dd>
								</div>
								<div>
									<dt>{SUMMARY.slowdowns}</dt>
									<dd>
										{live.slowdowns > 0 && live.lastSlowdownAt !== null
											? SUMMARY.slowdownsLine(fmtInt(live.slowdowns), clock(live.lastSlowdownAt))
											: SUMMARY.slowdownsNone}
									</dd>
								</div>
							</dl>
						</Section>
					) : null}
					{parallel && live ? (
						<Section title={SUMMARY.stretches}>
							<StretchTable vm={vm} live={live} />
						</Section>
					) : null}
					{live ? (
						<Section title={SUMMARY.events}>
							{live.events.length === 0 ? (
								<p class="dim">{SUMMARY.eventsNone}</p>
							) : (
								<ul class="run-summary-events" data-testid="run-summary-events">
									{live.events.map((e, i) => (
										<li key={`${e.at}-${i}`}>
											<time>{clock(e.at)}</time>
											<ChannelIdsIn text={eventText(vm, e)} />
										</li>
									))}
								</ul>
							)}
						</Section>
					) : null}
					{job || budget ? (
						<Section title={SUMMARY.cost}>
							<p>
								{SUMMARY.costLine(
									fmt2(budget?.spentUsd ?? job?.spentUsd ?? 0),
									fmt2(budget?.limitUsd ?? job?.limitUsd ?? 0),
									fmtInt(job?.usage.requests ?? 0)
								)}
							</p>
							{job ? (
								<p class="dim">
									{SUMMARY.tokens(
										fmtInt(job.usage.inputTokens),
										fmtInt(job.usage.outputTokens),
										fmtInt(job.usage.cacheReadTokens)
									)}
								</p>
							) : null}
						</Section>
					) : null}
					<Section title={SUMMARY.whatNext}>
						<p class="dim">{running ? SUMMARY.whatNextRunning : SUMMARY.whatNextDone}</p>
						{running && parallel ? <p class="dim">{SUMMARY.whatNextRounds}</p> : null}
					</Section>
				</div>
			)}
		</Dialog>
	);
}
